import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useInbox } from '@/contexts/InboxContext';
import { inboxPath, inboxError } from '@/lib/inbox';
import NotificationRow from '@/components/inbox/NotificationRow';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export default function NotificationBell() {
  const { profile } = useAuth();
  const inbox = useInbox();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const loadRecent = inbox.loadRecent;
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const data = await loadRecent(); setSnapshot(data.snapshot_at); }
    catch (err) { setError(inboxError(err, 'Could not load notifications.')); }
    finally { setLoading(false); }
  }, [loadRecent]);
  useEffect(() => { if (open) load(); }, [open, inbox.revision, load]);
  const markAll = async () => {
    if (!snapshot) return;
    setBusy(true);
    try { await inbox.action({action:'read', scope:'all', snapshot_at:snapshot}); }
    catch (err) { toast.error(inboxError(err, 'Could not mark notifications read.')); }
    finally { setBusy(false); }
  };
  return <DropdownMenu open={open} onOpenChange={setOpen}>
    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="relative text-white/70 hover:bg-white/10 hover:text-white" data-testid="topbar-notifications" aria-label={inbox.unreadCount ? `${inbox.unreadCount} unread notifications` : 'Notifications'}><Bell className="size-[18px]" />{inbox.unreadCount > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-[#D4AF37] px-1 text-[10px] font-bold leading-5 text-[#062B1A]">{inbox.unreadCount > 99 ? '99+' : inbox.unreadCount}</span>}</Button></DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-[min(420px,calc(100vw-24px))] overflow-hidden p-0">
      <header className="flex items-center justify-between gap-3 border-b px-4 py-3"><div><h2 className="text-sm font-semibold text-[#062B1A]">Notifications</h2><p className="text-xs text-slate-500">{inbox.unreadCount} unread</p></div><Button variant="ghost" size="sm" onClick={markAll} disabled={!inbox.unreadCount || !snapshot || busy}><CheckCheck className="mr-1.5 size-4" />Mark all read</Button></header>
      <div className="max-h-[440px] overflow-y-auto">
        {error ? <div role="alert" className="p-4 text-sm text-red-700">{error}<Button variant="ghost" size="sm" onClick={load}>Retry</Button></div> : loading && !inbox.recent.length ? <div className="flex justify-center p-10"><Loader2 className="size-5 animate-spin" /></div> : !inbox.recent.length ? <p className="p-10 text-center text-sm text-slate-500">You’re all caught up.</p> : inbox.recent.map(item => <NotificationRow key={item.id} item={item} compact onOpen={row => { setOpen(false); inbox.open(row); }} />)}
      </div>
      <footer className="border-t p-2"><Button variant="ghost" className="w-full text-[#062B1A]" onClick={() => { setOpen(false); navigate(inboxPath(profile)); }}>View all notifications</Button></footer>
    </DropdownMenuContent>
  </DropdownMenu>;
}
